/**
 * @fileoverview 顶部导航栏组件
 * @description 全站顶部固定导航栏，包含品牌 Logo、主导航链接、语言切换、用户菜单
 * 设计风格：币安深黑配色，黄色品牌色，固定在页面顶部
 * @module components/layout/Navbar
 */

import {
  BarChart3,
  Globe,
  Home,
  LayoutDashboard,
  LogOut,
  Menu,
  Send,
  Trophy,
  User,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'wouter';
import { useAppStore } from '../../stores/useAppStore';
// useWalletLogin 已移除：TG 环境下 wagmi/connectors 会导致 crypto.subtle 崩溃
// 登出时直接调用 logout() 即可，EVM 钉包断开逐渐迁移到 Wallet 页面内按需处理
import { cn } from '../../lib/utils';
import { updateLanguageCode } from '../../services/userService';
import { useMarketStore } from '../../stores/useMarketStore';
import { API_OK } from '../../services/api';
import { TG_MINIAPP_URL } from '../../lib/tgConfig';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Identicon } from '../ui/Identicon';

// ============================================================
// 导航链接配置
// ============================================================

/**
 * 主导航链接配置项接口
 */
interface NavItem {
  /** 路由路径 */
  path: string;
  /** 国际化 key */
  labelKey: string;
  /** 图标组件 */
  icon: React.ComponentType<{ className?: string }>;
}

/** 主导航链接列表 */
const NAV_ITEMS: NavItem[] = [
  { path: '/', labelKey: 'nav.home', icon: Home },
  { path: '/markets', labelKey: 'nav.markets', icon: BarChart3 },
  { path: '/portfolio', labelKey: 'nav.portfolio', icon: LayoutDashboard },
  { path: '/leaderboard', labelKey: 'nav.leaderboard', icon: Trophy },
  { path: '/wallet', labelKey: 'nav.wallet', icon: Wallet },
  { path: '/invite', labelKey: 'invite.navLabel', icon: Users },
];

// ============================================================
// 子组件：语言切换器
// ============================================================

/**
 * 语言切换下拉菜单组件
 * 支持中文和英文切换，持久化到 localStorage
 */
function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language.startsWith('zh') ? 'zh' : 'en';

  const handleSwitch = async (lang: string) => {
    // 1. 切换前端语言并持久化
    i18n.changeLanguage(lang);
    localStorage.setItem('augurx_language', lang);
    // 2. 上报语言到服务器（zh → zh-hant，en → en）
    const serverLang = lang === 'zh' ? 'zh-hant' : 'en';
    console.log('[LanguageSwitcher] 上报语言到服务器:', serverLang);
    try {
      const resp = await updateLanguageCode(serverLang);
      console.log('[LanguageSwitcher] 语言上报结果:', resp.status, resp.data);
      if (resp.status === API_OK) {
        // 3. 上报成功后重新请求市场列表
        useMarketStore.getState().fetchMarkets(0).catch(() => {});
      }
    } catch (e) {
      console.error('[LanguageSwitcher] 语言上报失败:', e);
      // 上报失败也尝试刷新市场
      useMarketStore.getState().fetchMarkets(0).catch(() => {});
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground hover:text-foreground">
          <Globe className="h-4 w-4" />
          <span className="text-xs font-medium">{currentLang === 'zh' ? t('nav.langZh') : t('nav.langEn')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-28 bg-surface-2 border-border">
        <DropdownMenuItem
          onClick={() => handleSwitch('zh')}
          className={cn('cursor-pointer', currentLang === 'zh' && 'text-brand')}
        >
          繁體中文
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleSwitch('en')}
          className={cn('cursor-pointer', currentLang === 'en' && 'text-brand')}
        >
          English
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ============================================================
// 子组件：用户菜单
// ============================================================

/**
 * 用户头像下拉菜单组件
 * 已登录时显示用户信息和操作菜单
 */
function UserMenu() {
  const { t } = useTranslation();
  const { currentUser, logout } = useAppStore();

  if (!currentUser) return null;

  /** 登出：清除 Token。TG 环境下无 EVM 钉包连接，无需 disconnect */
  const handleLogout = () => {
    logout();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-surface-2 transition-colors">
          {currentUser.avatar ? (
            <Avatar className="h-7 w-7">
              <AvatarImage src={currentUser.avatar} />
              <AvatarFallback className="bg-surface-3">
                <Identicon guid={currentUser.id} size={28} />
              </AvatarFallback>
            </Avatar>
          ) : (
            <Identicon guid={currentUser.id} size={28} className="rounded-full" />
          )}
          <span className="text-sm font-medium text-foreground hidden md:block max-w-24 truncate">
            {currentUser.username}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 bg-surface-2 border-border">
        <div className="px-3 py-2 border-b border-border">
          <p className="text-sm font-semibold text-foreground truncate">{currentUser.username}</p>
          <p className="text-xs text-muted-foreground truncate">{currentUser.email}</p>
        </div>
        <DropdownMenuItem asChild>
          <Link href="/profile" className="flex items-center gap-2 cursor-pointer">
            <User className="h-4 w-4" />
            {t('nav.profile')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/wallet" className="flex items-center gap-2 cursor-pointer">
            <Wallet className="h-4 w-4" />
            {t('nav.wallet')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => window.open(TG_MINIAPP_URL, '_blank')}
          className="flex items-center gap-2 cursor-pointer"
        >
          <Send className="h-4 w-4" />
          {t('nav.telegram')}
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-border" />
        <DropdownMenuItem
          onClick={handleLogout}
          className="text-danger cursor-pointer flex items-center gap-2"
        >
          <LogOut className="h-4 w-4" />
          {t('nav.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ============================================================
// 主组件：顶部导航栏
// ============================================================

/**
 * 顶部导航栏主组件
 * 固定在页面顶部，包含 Logo、导航链接、语言切换、用户菜单
 * 移动端显示汉堡菜单
 */
export default function Navbar() {
  const { t } = useTranslation();
  const [location] = useLocation();
  const { isAuthenticated } = useAppStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  /**
   * 判断当前路由是否匹配导航项
   * 首页（/）使用精确匹配，其他路由支持前缀匹配
   * @param path - 导航项路径
   * @returns 是否为当前激活路由
   */
  const isActive = (path: string): boolean => {
    if (path === '/') return location === '/';
    return location === path || location.startsWith(path + '/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0B0E11]/96 backdrop-blur-md border-b border-white/6" style={{ boxShadow: '0 1px 0 rgba(240,185,11,0.06)' }}>
      <div className="container">
        <div className="flex items-center justify-between h-15" style={{ height: '60px' }}>
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 shrink-0">
            <img src="data:image/png;base64,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" alt="AugurX" className="w-8 h-8 rounded-lg object-cover" />
            <span className="font-black text-lg text-white tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-0.02em' }}>
              AugurX
            </span>
          </Link>

          {/* 桌面端主导航 */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  href={item.path}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                    isActive(item.path)
                      ? 'text-brand bg-brand/10'
                      : 'text-muted-foreground hover:text-foreground hover:bg-surface-2'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {t(item.labelKey)}
                </Link>
              );
            })}
          </nav>

          {/* 右侧操作区 */}
          <div className="flex items-center gap-2">
            <LanguageSwitcher />

            {isAuthenticated ? (
              <UserMenu />
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <Button size="sm" className="btn-brand" asChild>
                  <Link href="/login">{t('nav.login')}</Link>
                </Button>
              </div>
            )}

            {/* 移动端汉堡菜单按钮 */}
            <button
              className="md:hidden p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-surface-2 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* 移动端展开菜单 */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#0B0E11] border-t border-border">
          <nav className="container py-3 flex flex-col gap-1">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  href={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium transition-colors',
                    isActive(item.path)
                      ? 'text-brand bg-brand/10'
                      : 'text-muted-foreground hover:text-foreground hover:bg-surface-2'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {t(item.labelKey)}
                </Link>
              );
            })}
            {!isAuthenticated && (
              <div className="flex gap-2 pt-2 border-t border-border mt-1">
                <Button size="sm" className="btn-brand flex-1" asChild>
                  <Link href="/login" onClick={() => setMobileMenuOpen(false)}>{t('nav.login')}</Link>
                </Button>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
